import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import ComingSoonModal from './ComingSoonModal';

type PrizeCard =
  | { type: 'name'; name: string }
  | { type: 'value'; label: string; value: string };

const topRow: PrizeCard[] = [
  { type: 'name', name: 'Titleist Golf Club Set' },
  { type: 'value', label: 'Prize Value', value: '$3,500+' },
  { type: 'name', name: 'Pebble Beach Trip for Two' },
];

const bottomRow: PrizeCard[] = [
  { type: 'name', name: 'TaylorMade Golf Club Set' },
  { type: 'name', name: 'Lifetime Supply of Pro V1s' },
  { type: 'value', label: 'Prize Value', value: '$3,500+' },
];

const topRowCards = [...topRow, ...topRow, ...topRow, ...topRow];
const bottomRowCards = [...bottomRow, ...bottomRow, ...bottomRow, ...bottomRow];

const PrizeCardItem: React.FC<{ card: PrizeCard }> = ({ card }) => (
  <div className="flex-shrink-0 w-28 h-36 sm:w-44 sm:h-52 mr-3 sm:mr-4 rounded-2xl overflow-hidden shadow-lg bg-white border border-gray-100">
    <div className="flex flex-col items-center justify-center h-full p-4 text-center">
      {card.type === 'name' ? (
        <p className="font-bold text-sm sm:text-base text-gray-900">{card.name}</p>
      ) : (
        <>
          <p className="text-xs sm:text-sm text-gray-500 mb-1">{card.label}</p>
          <p className="font-bold text-xl sm:text-2xl" style={{ color: '#9CB75A' }}>{card.value}</p>
        </>
      )}
    </div>
  </div>
);

const Hero: React.FC = () => {
  const [isComingSoonModalOpen, setIsComingSoonModalOpen] = useState(false);

  return (
    <section className="relative bg-white overflow-hidden">
      {/* Navigation - Floating pill style */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 pt-3">
        <div className="max-w-md sm:max-w-lg mx-auto bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-200/60 px-4 py-2.5 flex items-center justify-between">
          {/* Left: Logo + App Name */}
          <a href="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 shadow-sm border border-gray-100">
              <img
                src="/branding/app-icon.png"
                alt="Hole In 1 Logo"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="font-bold text-lg text-gray-900">Hole In 1</span>
          </a>

          {/* Right: App Store button */}
          <button
            onClick={() => setIsComingSoonModalOpen(true)}
            className="hover:opacity-80 transition-opacity flex-shrink-0"
            aria-label="Download on the App Store"
          >
            <img
              src="/branding/app-store-icon.svg"
              alt="App Store"
              className="w-10 h-10 rounded-xl"
            />
          </button>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="container mx-auto px-2 sm:px-6 lg:px-8 pt-36 sm:pt-40 lg:pt-44 pb-16 sm:pb-20 lg:pb-24">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-[2.5rem] sm:text-6xl md:text-7xl lg:text-8xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            Make a Hole In One
            <span className="block" style={{ color: '#9CB75A' }}>Win Real Prizes</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-8 sm:mb-10">
            The app that turns every par-3 hole into a chance to win. Record your tee shot, and if it goes in, the prize is yours.
          </p>

          {/* CTA Buttons - Centered */}
          <div className="flex flex-col items-center mb-[168px] sm:mb-48">
            <button
              onClick={() => setIsComingSoonModalOpen(true)}
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src="/branding/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
                alt="Download on the App Store"
                className="h-16 sm:h-20"
              />
            </button>
            <span className="mt-3 inline-block px-4 py-1.5 text-sm font-medium text-gray-500 bg-gray-100 rounded-full">
              Launching in Utah County
            </span>
          </div>

          {/* Phone Mockup with Animated Prize Cards */}
          <div className="relative flex justify-center overflow-hidden mx-auto" style={{ maxWidth: '600px' }}>
            {/* Animated prize card rows behind phone */}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 sm:gap-4 pointer-events-none z-0">
              <div className="flex w-max will-change-transform animate-marquee-left">
                {[...topRowCards, ...topRowCards].map((card, i) => (
                  <PrizeCardItem key={`top-${i}`} card={card} />
                ))}
              </div>
              <div className="flex w-max will-change-transform animate-marquee-right">
                {[...bottomRowCards, ...bottomRowCards].map((card, i) => (
                  <PrizeCardItem key={`bottom-${i}`} card={card} />
                ))}
              </div>
            </div>

            <div className="absolute inset-y-0 left-0 w-16 sm:w-24 z-[1] pointer-events-none bg-gradient-to-r from-white to-transparent" />
            <div className="absolute inset-y-0 right-0 w-16 sm:w-24 z-[1] pointer-events-none bg-gradient-to-l from-white to-transparent" />

            {/* Phone mockup - on top of cards */}
            <div className="relative z-10 w-48 h-[390px] sm:w-80 sm:h-[650px]">
              <img
                src="/features/iphone-onboarding.png"
                alt="iPhone frame"
                className="absolute inset-0 w-full h-full object-contain z-10 pointer-events-none"
              />
              <div className="absolute left-[5.5%] right-[5.5%] top-[2%] bottom-[2%] overflow-hidden rounded-[2.5rem]">
                <img
                  src="/features/hero-image.PNG"
                  alt="Hole In 1 App - Win Real Prizes"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <a
            href="#how-it-works"
            className="inline-flex flex-col items-center mt-12 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <span className="text-sm font-medium mb-1">Learn More</span>
            <ChevronDown size={24} className="animate-bounce-slow" />
          </a>
        </div>
      </div>

      {/* Coming Soon Modal */}
      <ComingSoonModal
        isOpen={isComingSoonModalOpen}
        onClose={() => setIsComingSoonModalOpen(false)}
      />
    </section>
  );
};

export default Hero;
